import {
  ThresholdRule,
  ThresholdColors,
  Threshold,
  ThresholdColor,
} from '../types/threshold-types.js';

/** Returns whether a given value matches a given threshold.
 * @internal
 */
export function _matchesThreshold(
  value: number | string,
  threshold: Threshold | ThresholdRule,
): boolean {
  if (typeof value === 'string') {
    if (threshold.comparator === 'equal-to') {
      return value === threshold.value;
    } else if (threshold.comparator === 'not-equal-to') {
      return value !== threshold.value;
    }

    // oxlint-disable-next-line no-console
    console.warn(
      `Threshold comparator ${threshold.comparator} is not supported for type string`,
    );
    return false;
  }
  // stored in the variable here because typescript wrongly interpreted the default value as 'never' and was showing an error
  const comparator = threshold.comparator;
  switch (comparator) {
    case 'greater-than':
      return value > threshold.value;
    case 'less-than':
      return value < threshold.value;
    case 'greater-than-or-equal-to':
      return value >= threshold.value;
    case 'less-than-or-equal-to':
      return value <= threshold.value;
    case 'equal-to':
      return value === threshold.value;
    case 'not-equal-to':
      return value !== threshold.value;
    default:
      // oxlint-disable-next-line no-console
      console.log(`Threshold comparator not supported: ${comparator}`);
      return false;
  }
}

/**
 * Returns the color of the threshold configuration that matches a given value or
 * `null` if no threshold matches.
 *
 * @param value - The value to compare the threshold value(s) to
 * @param thresholds - The thresholds to test the value against
 * @param colorFn - Function that gets called whenever a value matches a threshold
 * @returns the threshold color for that value or `null` if none applies
 *
 * @deprecated This helper function will be removed in 2.0.0.
 * @public
 */
export function getThresholdColor(
  value: number | string,
  thresholds: Threshold[],
  forBackgroundColor: boolean = false,
  colorFn?: (threshold: Threshold) => ThresholdColor,
): ThresholdColor | null {
  for (let i = thresholds.length - 1; i >= 0; i--) {
    const threshold = thresholds[i];

    if (_matchesThreshold(value, threshold)) {
      const color = colorFn
        ? colorFn(threshold)
        : forBackgroundColor
          ? threshold.backgroundColor
          : threshold.color;
      return color ?? null;
    }
  }

  return null;
}

/**
 * Returns the color of the threshold configuration when all combined rules are 'true'.
 * When some of applied rules are false then the function will return `null`.
 * @param combinedRules - Multiple thresholds to check the value against
 * @param allIds - All ids to specify which object thresholds should apply to
 * @param values - Values to compare the thresholds values to
 * @param color - Threshold color
 * @returns color or null
 * @internal
 */
export function _checkCombinedThresholdRules(
  combinedRules: ThresholdRule[],
  allIds: string[],
  values: Record<string, string | number>,
  color?: string | undefined,
  backgroundColor?: string | undefined,
): ThresholdColors {
  let thresholdColor: string | undefined;
  let thresholdBackgroundColor: string | undefined;

  for (let rule = combinedRules.length - 1; rule >= 0; rule--) {
    const currentRule = combinedRules[rule];
    const foundId = allIds.find((key) => currentRule.id === key);
    // Apply the threshold only if you find matching id.
    if (foundId) {
      const passedThreshold = _matchesThreshold(values[foundId], currentRule);

      // Assign the threshold color only if all combined threshold rules are true.
      if (passedThreshold) {
        thresholdColor = color;
        thresholdBackgroundColor = backgroundColor;
      } else {
        return {
          thresholdColor: undefined,
          thresholdBackgroundColor: undefined,
        };
      }
    } else {
      return { thresholdColor: undefined, thresholdBackgroundColor: undefined };
    }
  }
  return { thresholdColor, thresholdBackgroundColor };
}
